import { t } from "i18next";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { DatePickerText } from "../../../../core/components/input/DatePickerText";
import { TextInput } from "../../../../core/components/input/TextInput";
import {
  getIsReleaseSession,
  getPracticePatientPersonalInfo,
  getUserAnswerToQuestion,
} from "../../../../core/store/currentFormSlice";
import { useMainStore } from "../../../../core/store/mainStore";
import { calendarDay } from "../../utils/personalInfoChange";

type Props = {
  answerId: string;
  questionId: string;
  noAddress?: boolean;
};

export const PersonalInfoAnswer = ({
  answerId,
  questionId,
  noAddress,
}: Props) => {
  const currentZustandPersonalInfo = useMainStore(
    getPracticePatientPersonalInfo,
  );
  const viaRelease = useMainStore(getIsReleaseSession);
  // The birthday the release was redeemed with (PF-6133), as the server compared it; "" when the record holds none.
  const verifiedBirthday = viaRelease
    ? calendarDay(currentZustandPersonalInfo?.birthday)
    : "";

  const patientAnswer = useMainStore((state) =>
    getUserAnswerToQuestion(state, answerId),
  );

  const [fieldValues, setFieldValues] = useState<
    | {
        firstName?: string;
        lastName?: string;
        birthday?: string;
        email?: string;
        address?: { street?: string; zip?: string; city?: string };
      }
    | undefined
  >(currentZustandPersonalInfo);

  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );
  const deleteUserAnswerToQuestion = useMainStore(
    (state) => state.deleteUserAnswerToQuestion,
  );

  const handleFieldChange = (
    fieldKey:
      | "firstName"
      | "lastName"
      | "birthday"
      | "email"
      | "addressStreet"
      | "addressZip"
      | "addressCity",
    value: string,
  ) => {
    setFieldValues((prev) => {
      if (fieldKey.startsWith("address")) {
        const addressField = fieldKey.replace("address", "").toLowerCase();
        return {
          ...prev,
          address: {
            ...prev?.address,
            [addressField]: value,
          },
        };
      } else {
        return {
          ...prev,
          [fieldKey]: value,
        };
      }
    });
  };

  const saveUserPersonalInfos = () => {
    if (fieldValues) {
      const requiredFieldsFilled =
        fieldValues &&
        !!fieldValues.firstName &&
        !!fieldValues.lastName &&
        !!fieldValues.birthday &&
        (noAddress ||
          (!!fieldValues.address &&
            !!fieldValues.address.street &&
            !!fieldValues.address.zip &&
            !!fieldValues.address.city));

      if (requiredFieldsFilled) {
        const patientAnswer = {
          id: answerId,
          personalInfoDetails: {
            firstName: fieldValues.firstName!,
            lastName: fieldValues.lastName!,
            birthday: fieldValues.birthday!,
            // Saved as "" when blank, never left out: savePersonalInfo copies
            // it onto the practicePatients record, and signPatientDocument's
            // signerInfo write fails on a missing value.
            email: fieldValues.email ?? "",
            ...(noAddress
              ? {}
              : {
                  address: {
                    street: fieldValues.address!.street!,
                    zip: fieldValues.address!.zip!,
                    city: fieldValues.address!.city!,
                  },
                }),
          },
        };
        setUserAnswerToQuestion(questionId, patientAnswer, answerId);
      } else {
        deleteUserAnswerToQuestion(questionId, answerId);
      }
    }
  };

  useEffect(() => {
    saveUserPersonalInfos();
  }, [currentZustandPersonalInfo, patientAnswer, fieldValues]);

  return (
    <VerticalContainer>
      <TextInput
        label={t("APPOINTMENT_FIRST_NAME")}
        placeholder={t("APPOINTMENT_FIRST_NAME")}
        type="text"
        value={fieldValues?.firstName || ""}
        onChange={(text) => handleFieldChange("firstName", text)}
      />
      <TextInput
        label={t("APPOINTMENT_LAST_NAME")}
        placeholder={t("APPOINTMENT_LAST_NAME")}
        type="text"
        value={fieldValues?.lastName || ""}
        onChange={(text) => handleFieldChange("lastName", text)}
      />
      {verifiedBirthday ? (
        <TextInput
          label={t("APPOINTMENT_BIRTHDAY")}
          placeholder={t("APPOINTMENT_BIRTHDAY")}
          type="text"
          value={verifiedBirthday.split("-").reverse().join(".")}
          disabled
        />
      ) : (
        <DatePickerText
          date={fieldValues?.birthday}
          onChange={(value) => handleFieldChange("birthday", value)}
          label={t("APPOINTMENT_BIRTHDAY")}
        />
      )}
      <TextInput
        label={t("APPOINTMENT_EMAIL")}
        tag={t("OPTIONAL")}
        placeholder={t("APPOINTMENT_EMAIL")}
        type="email"
        value={fieldValues?.email || ""}
        onChange={(text) => handleFieldChange("email", text)}
      />
      {!noAddress && (
        <>
          <TextInput
            label={t("STREET")}
            placeholder={t("STREET")}
            type="text"
            value={fieldValues?.address?.street || ""}
            onChange={(text) => handleFieldChange("addressStreet", text)}
          />
          <HorizontalContainer>
            <TextInput
              label={t("ZIP")}
              placeholder={t("ZIP")}
              type="text"
              inputMode="numeric"
              value={fieldValues?.address?.zip || ""}
              onChange={(text) => handleFieldChange("addressZip", text)}
            />
            <TextInput
              label={t("CITY")}
              placeholder={t("CITY")}
              type="text"
              value={fieldValues?.address?.city || ""}
              onChange={(text) => handleFieldChange("addressCity", text)}
            />
          </HorizontalContainer>
        </>
      )}
    </VerticalContainer>
  );
};

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 10px;
`;
